import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowDownward as DownIcon,
  ArrowUpward as UpIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';

/**
 * Ordered author list for a publication. Every consortium member is an author
 * automatically (the server keeps them in the list); this panel arranges the order,
 * marks corresponding authors and adds outside co-authors. onSave({ authors }) persists it.
 */
const AuthorsPanel = ({ pub, members, canEdit, onSave }) => {
  const [authors, setAuthors] = useState([]);
  const [external, setExternal] = useState({ name: '', affiliation: '', email: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { setAuthors(pub.authors || []); }, [pub]);

  const dirty = JSON.stringify(authors) !== JSON.stringify(pub.authors || []);

  const update = (i, patch) => setAuthors((prev) => prev.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  const move = (i, delta) => setAuthors((prev) => {
    const next = [...prev];
    const [item] = next.splice(i, 1);
    next.splice(i + delta, 0, item);
    return next;
  });

  const addExternal = () => {
    if (!external.name.trim()) return;
    setAuthors((prev) => [...prev, { user_id: null, ...external, name: external.name.trim(), is_corresponding: prev.length === 0 }]);
    setExternal({ name: '', affiliation: '', email: '' });
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave({ authors });
    setSaving(false);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        All consortium members are authors automatically. Arrange the order as it should appear on the manuscript, mark the
        corresponding author(s), and add co-authors from outside the consortium below.
      </Typography>

      {authors.length === 0 && <Alert severity="info" sx={{ mb: 2 }}>No authors yet.</Alert>}

      {authors.map((a, i) => (
        <Stack key={`${a.user_id || 'ext'}-${i}`} direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}
          sx={{ py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
          <Typography sx={{ width: 28, fontWeight: 600 }}>{i + 1}.</Typography>
          <TextField size="small" label="Name" value={a.name} onChange={(e) => update(i, { name: e.target.value })}
            disabled={!canEdit || Boolean(a.user_id)} sx={{ flex: 1.2 }} />
          <TextField size="small" label="Affiliation" value={a.affiliation || ''} onChange={(e) => update(i, { affiliation: e.target.value })}
            disabled={!canEdit} sx={{ flex: 1.5 }} />
          <TextField size="small" label="Email" value={a.email || ''} onChange={(e) => update(i, { email: e.target.value })}
            disabled={!canEdit} sx={{ flex: 1.2 }} />
          <FormControlLabel
            control={<Checkbox size="small" checked={Boolean(a.is_corresponding)} disabled={!canEdit}
              onChange={(e) => update(i, { is_corresponding: e.target.checked })} />}
            label="Corresponding"
          />
          <Chip size="small" label={a.user_id ? 'Member' : 'External'} variant="outlined" color={a.user_id ? 'primary' : 'default'} />
          {canEdit && (
            <Stack direction="row">
              <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)}><UpIcon fontSize="small" /></IconButton>
              <IconButton size="small" disabled={i === authors.length - 1} onClick={() => move(i, 1)}><DownIcon fontSize="small" /></IconButton>
              {/* Members leave the author list only by leaving the consortium */}
              <IconButton size="small" color="error" disabled={Boolean(a.user_id)}
                onClick={() => setAuthors((prev) => prev.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>
            </Stack>
          )}
        </Stack>
      ))}

      {canEdit && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }}>
          <TextField size="small" label="External author name" value={external.name}
            onChange={(e) => setExternal((p) => ({ ...p, name: e.target.value }))} sx={{ flex: 1 }} />
          <TextField size="small" label="Affiliation" value={external.affiliation}
            onChange={(e) => setExternal((p) => ({ ...p, affiliation: e.target.value }))} sx={{ flex: 1 }} />
          <TextField size="small" label="Email (optional)" value={external.email}
            onChange={(e) => setExternal((p) => ({ ...p, email: e.target.value }))} sx={{ flex: 1 }} />
          <Button startIcon={<AddIcon />} onClick={addExternal} disabled={!external.name.trim()}>Add external author</Button>
        </Stack>
      )}

      {canEdit && (
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 3 }}>
          <Button onClick={() => setAuthors(pub.authors || [])} disabled={!dirty || saving}>Discard</Button>
          <Button variant="contained" startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />} onClick={handleSave} disabled={!dirty || saving}>
            Save authors
          </Button>
        </Stack>
      )}
    </Paper>
  );
};

export default AuthorsPanel;
